import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ApiError } from "./lib/api";
import { useMe } from "./lib/queries";
import { applyUpdate, initPwa, onUpdateAvailable } from "./lib/pwa";
import { SessionProvider } from "./hooks/useSession";
import { PeriodProvider } from "./hooks/usePeriod";
import { UiProvider } from "./hooks/useUi";
import { SelectedBoutiqueProvider, useSelectedBoutique } from "./hooks/useSelectedBoutique";
import { useViewMode } from "./hooks/useViewMode";
import { DesktopShell } from "./layouts/DesktopShell";
import { MobileShell } from "./layouts/MobileShell";
import { Loading } from "./components/ui";
import { ComposeDrawer } from "./components/ComposeDrawer";
import { ProductSheetDrawer } from "./components/ProductSheetDrawer";
import { LoginPage } from "./routes/Login";
import { InvitationPage } from "./routes/Invitation";
import { OnboardingPage } from "./routes/Onboarding";
import { DashboardPage } from "./routes/Dashboard";
import { BoutiquesPage } from "./routes/Boutiques";
import { BoutiquePage } from "./routes/Boutique";
import { StocksPage } from "./routes/Stocks";
import { ProductSheetPage } from "./routes/ProductSheet";
import { ArrivagesPage } from "./routes/Arrivages";
import { MessagesPage } from "./routes/Messages";
import { PromosPage } from "./routes/Promos";
import { AccessPage } from "./routes/Access";
import { SettingsPage } from "./routes/Settings";
import { ProfilePage } from "./routes/Profile";
import { PlatformPage } from "./routes/Platform";
import { AdministrationPage } from "./routes/Administration";
import { PlanningPage } from "./routes/Planning";
import { MobilePlanning } from "./routes/mobile/Planning";
import { MobileHome } from "./routes/mobile/Home";
import { MobileStock } from "./routes/mobile/Stock";
import { MobileSales } from "./routes/mobile/Sales";
import { MobileMessages } from "./routes/mobile/Messages";
import { MobileTasks } from "./routes/mobile/Tasks";
import { MobileArrivals } from "./routes/mobile/Arrivals";
import { MobilePromos } from "./routes/mobile/Promos";
import { MobileAccess, MobilePlus, MobileProfile } from "./routes/mobile/Plus";
import { ItPage } from "./routes/It";

export function App() {
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => {
    initPwa();
    return onUpdateAvailable(() => setUpdateReady(true));
  }, []);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invitation/:token" element={<InvitationPage />} />
        <Route path="/*" element={<Authenticated />} />
      </Routes>
      {updateReady && (
        <div className="fixed bottom-[calc(90px+env(safe-area-inset-bottom))] left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full bg-sidebar px-5 py-3 text-[13px] text-white shadow-menu">
          Nouvelle version disponible
          <button type="button" className="font-bold underline" onClick={applyUpdate}>
            Mettre à jour
          </button>
        </div>
      )}
    </>
  );
}

function Authenticated() {
  const me = useMe({ retry: false });
  const loc = useLocation();
  if (me.isLoading) return <Loading label="Chargement de Pilote…" className="pt-[30vh]" />;
  if (me.error instanceof ApiError && me.error.status === 401) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }
  if (me.error || !me.data) return <Loading label="Connexion à l'API impossible. Nouvel essai…" className="pt-[30vh]" />;
  if (me.data.approval !== "APPROVED" || !me.data.organization) return <OnboardingPage me={me.data} />;
  return (
    <SessionProvider me={me.data}>
      <PeriodProvider>
        <UiProvider>
          <SelectedBoutiqueProvider>
            <AppRoutes />
            <ComposeDrawer />
            <ProductSheetDrawer />
          </SelectedBoutiqueProvider>
        </UiProvider>
      </PeriodProvider>
    </SessionProvider>
  );
}

/** Page PC → équivalent mobile (liens de notification, favoris, retour de la vue PC). */
function toMobilePath(pathname: string, search: string, hash: string): string | null {
  const params = new URLSearchParams(search);
  const boutique = pathname.match(/^\/boutiques\/([^/]+)/)?.[1];
  if (boutique) {
    const tab = params.get("tab");
    const page = { produits: "stock", stock: "stock", ventes: "ventes", arrivages: "arrivages", planning: "planning", messages: "messages", taches: "taches" }[tab ?? ""];
    return `/m${page ? `/${page}` : ""}?boutique=${encodeURIComponent(boutique)}`;
  }
  const map: Record<string, string> = {
    "/": "/m",
    "/boutiques": "/m",
    "/produits": "/m/stock",
    "/stocks": "/m/stock",
    "/arrivages": "/m/arrivages",
    "/messages": params.get("tab") === "suivi" ? "/m/taches" : "/m/messages",
    "/promos": "/m/promos",
    "/planning": "/m/planning",
    "/acces": "/m/acces",
    "/it": "/m/it",
    "/profil": "/m/profil",
  };
  const to = map[pathname.replace(/\/+$/, "") || "/"];
  if (!to) return null;
  // Les messages gardent ?boutique= / ?conversation= ; le profil garde #reseaux.
  return to + (to === "/m/messages" ? search : "") + (to === "/m/profil" ? hash : "");
}

/** Page mobile → équivalent PC (écran élargi). */
function toDesktopPath(pathname: string, selectedId: string | null): string {
  const page = pathname.replace(/^\/m\/?/, "").split("/")[0];
  const map: Record<string, string> = {
    stock: "/produits",
    ventes: selectedId ? `/boutiques/${selectedId}?tab=ventes` : "/",
    planning: "/planning",
    messages: selectedId ? `/messages?boutique=${selectedId}` : "/messages",
    taches: "/messages?tab=suivi",
    promos: "/promos",
    arrivages: "/arrivages",
    acces: "/acces",
    it: "/it",
    profil: "/profil",
  };
  return map[page] ?? "/";
}

function AppRoutes() {
  const mode = useViewMode();
  const { selectedId } = useSelectedBoutique();
  const loc = useLocation();
  const inMobile = loc.pathname === "/m" || loc.pathname.startsWith("/m/");

  // Vue choisie par la largeur d'écran, pour tous les rôles. En mobile, les pages PC qui ont un
  // équivalent mobile y redirigent ; les autres (Paramètres, Tous les réseaux) restent en PC.
  if (mode === "mobile" && !inMobile) {
    const to = toMobilePath(loc.pathname, loc.search, loc.hash);
    if (to) return <Navigate to={to} replace />;
  }
  if (inMobile && mode === "desktop") return <Navigate to={toDesktopPath(loc.pathname, selectedId)} replace />;

  return (
    <Routes>
      <Route path="/m" element={<MobileShell />}>
        <Route index element={<MobileHome />} />
        <Route path="stock" element={<MobileStock />} />
        <Route path="ventes" element={<MobileSales />} />
        <Route path="messages" element={<MobileMessages />} />
        <Route path="planning" element={<MobilePlanning />} />
        <Route path="plus" element={<MobilePlus />} />
        <Route path="taches" element={<MobileTasks />} />
        <Route path="promos" element={<MobilePromos />} />
        <Route path="arrivages" element={<MobileArrivals />} />
        <Route path="acces" element={<MobileAccess />} />
        <Route path="it" element={<ItPage />} />
        <Route path="profil" element={<MobileProfile />} />
        <Route path="*" element={<Navigate to="/m" replace />} />
      </Route>
      <Route element={<DesktopShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="boutiques" element={<BoutiquesPage />} />
        <Route path="boutiques/:id" element={<BoutiquePage />} />
        <Route path="produits" element={<StocksPage />} />
        <Route path="produits/fiche" element={<ProductSheetPage />} />
        <Route path="stocks" element={<Navigate to="/produits" replace />} />
        <Route path="arrivages" element={<ArrivagesPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="promos" element={<PromosPage />} />
        <Route path="planning" element={<PlanningPage />} />
        <Route path="acces" element={<AccessPage />} />
        <Route path="it" element={<ItPage />} />
        <Route path="parametres" element={<SettingsPage />} />
        <Route path="profil" element={<ProfilePage />} />
        <Route path="reseaux" element={<PlatformPage />} />
        <Route path="administration" element={<AdministrationPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
